import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel,
  Button, IconButton, InputAdornment, Divider, FormControlLabel, Switch, Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import Swal from "sweetalert2";
import BentoCard from "components/bento/BentoCard";
import CustomerSearchAutocomplete from "components/CustomerSearchAutocomplete";
import { GetLatestRate } from "api/currencyExchangeRate";
import {
  CreateSalesDocument, PreviewSalesDocumentPdf, GetSalesDocumentPdfBlobUrl,
  SaveSalesDocumentDraft, UpdateSalesDocumentDraft, IssueSalesDocumentDraft, DiscardSalesDocumentDraft,
} from "api/salesDocument";
import PackagePickerDialog from "./PackagePickerDialog";
import PreviewDialog from "./PreviewDialog";
import { convertAmount } from "./currencyConvert";

const SYM = { THB: "฿", LAK: "₭", USD: "$" };
const emptyItem = () => ({ description: "", qty: 1, price: 0, deliveryFee: 0, shippingFee: 0, packageId: null });
const itemAmount = (i) => (Number(i.qty) || 0) * (Number(i.price) || 0) + (Number(i.deliveryFee) || 0) + (Number(i.shippingFee) || 0);
// LAK has no sub-unit in practice (smallest banknote is ₭1,000) — no decimals.
const fmtMoney = (n, currency) => (Number(n) || 0).toLocaleString(undefined, currency === "LAK" ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2 });

// `onCreated` — bumps the parent's refreshSignal so the History tab picks up
// the new document without a manual reload (same pattern as departureBatch).
// `seed` — { doc, seq, mode } owned by the parent (scenes/salesDocuments/index.jsx):
//   - mode "duplicate": set by History → Document Detail's "Duplicate" action.
//     All doc types can be duplicated into any doc type — the source's
//     docType is only the starting value of the selector, not a limit.
//   - mode "draft": set by the Drafts tab (and by onDraftSaved below) — the
//     form then edits THAT draft: Save Draft updates it, Confirm issues it.
//   `seq` changes on every click (even re-opening the same doc) so the
//   prefill effect below always re-applies. This tab is unmounted whenever
//   another tab is shown (MUI lab TabPanel), so on remount the seed is
//   re-applied too — which is why the parent keeps a draft seed's `doc`
//   updated on every save (`onDraftSaved`) and drops the seed on `onReset`.
const CreateTab = ({ onCreated, seed, onDraftSaved, onReset }) => {
  const { t } = useTranslation();

  const [docType, setDocType] = useState("receipt");
  // Receipt only — "one receipt = one customer": staff picks the customer
  // first, then browses/multi-selects that customer's packages via the
  // PackagePickerDialog modal (see handleAddPackages below).
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [customer, setCustomer] = useState({ customerId: "", name: "", address: "", contact: "" });
  const [items, setItems] = useState([emptyItem()]);
  const [currency, setCurrency] = useState("THB");
  const [hideFees, setHideFees] = useState(false);
  const [hideRate, setHideRate] = useState(false);
  const [termOfPayment, setTermOfPayment] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [note, setNote] = useState("");
  // Kept loaded regardless of the selected currency — packages are always
  // THB-priced, so converting a package's fees into LAK/USD (or re-converting
  // existing items when the currency selector changes) needs the rate on
  // hand no matter which currency is currently showing.
  const [exchangeRate, setExchangeRate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Set while the form is editing an existing draft (opened from the Drafts
  // tab, or after the first Save Draft) — null means a brand-new document.
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const noteTouchedRef = useRef(false);

  useEffect(() => {
    GetLatestRate().then((res) => setExchangeRate(res?.data || null));
  }, []);

  // Preview blob URLs are only ever referenced by the dialog while it's open
  // — revoke the previous one whenever it's replaced or the component unmounts.
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  // Pre-fill the pre-order disclaimer for Quotation/Invoice, same wording as
  // the paper templates — but only while the staff hasn't typed their own note.
  useEffect(() => {
    if (noteTouchedRef.current) return;
    if (docType === "quotation" || docType === "invoice") {
      setNote(t("salesDocuments.defaultNote"));
    } else {
      setNote("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType]);

  // Fills the whole form from an existing document — shared by Duplicate and
  // by opening a draft (whose customer/items may still be incomplete).
  const prefillFrom = (src) => {
    setDocType(src.docType);
    setCurrency(src.currency || "THB");
    setSelectedCustomer(src.customer?.customerId ? { _id: src.customer.customerId, name: src.customer.name, whatsapp: src.customer.contact } : null);
    setCustomer({
      customerId: src.customer?.customerId || "",
      name: src.customer?.name || "",
      address: src.customer?.address || "",
      contact: src.customer?.contact || "",
    });
    setItems(src.items?.length ? src.items.map((it) => ({
      description: it.description || "",
      qty: it.qty,
      price: it.price,
      deliveryFee: it.deliveryFee || 0,
      shippingFee: it.shippingFee || 0,
      packageId: it.packageId || null,
    })) : [emptyItem()]);
    setHideFees(!!src.hideFees);
    setHideRate(!!src.hideRate);
    setTermOfPayment(src.termOfPayment || "");
    setDueDate(src.dueDate ? new Date(src.dueDate).toISOString().slice(0, 10) : "");
    // Only mark the note as "touched" (so the docType-default-note effect
    // above won't overwrite it) when the source actually had one — an empty
    // source note still gets the usual default when landing on Quotation/Invoice.
    noteTouchedRef.current = !!src.note;
    setNote(src.note || "");
    setPreviewUrl(null);
  };

  // "duplicate": prefill the whole form from an existing document (any doc
  // type) so staff can issue a new one from it — e.g. reuse a Quotation's
  // data to generate an Invoice. Preview/Confirm below then create a
  // brand-new document with its own doc number, never touching the source.
  // "draft": open a saved draft — Save Draft / Confirm below then act on
  // this draft rather than creating a new document.
  useEffect(() => {
    if (!seed?.doc) return;
    prefillFrom(seed.doc);
    setEditingDraftId(seed.mode === "draft" ? seed.doc._id : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed?.seq]);

  const resetForm = () => {
    setSelectedCustomer(null);
    setCustomer({ customerId: "", name: "", address: "", contact: "" });
    setItems([emptyItem()]);
    setHideFees(false);
    setHideRate(false);
    setTermOfPayment("");
    setDueDate("");
    noteTouchedRef.current = false;
    setNote(docType === "quotation" || docType === "invoice" ? t("salesDocuments.defaultNote") : "");
    setPreviewUrl(null);
    setEditingDraftId(null);
    onReset?.();
  };

  // Picking a different customer starts a fresh item list — a Receipt is
  // "one customer" by design, so mixing in a previous customer's packages
  // would be a data-entry mistake, not a real use case.
  const handleCustomerChange = (nextCustomer) => {
    setSelectedCustomer(nextCustomer);
    setCustomer({
      customerId: nextCustomer?._id || "",
      name: nextCustomer?.name || "",
      address: "",
      contact: nextCustomer?.whatsapp || "",
    });
    setItems([emptyItem()]);
  };

  const handleAddPackages = (packages) => {
    setPickerOpen(false);
    if (packages.length === 0) return;
    // Package.amount/deliveryFee/shippingFee are already THB, and the form
    // always stays in THB regardless of the selected currency (see currency
    // FormControl below) — currency only affects the generated PDF.
    const newItems = packages.map((pkg) => ({
      description: pkg.description || "",
      qty: pkg.quantity || 1,
      price: pkg.amount || 0,
      deliveryFee: pkg.deliveryFee || 0,
      shippingFee: pkg.shippingFee || 0,
      packageId: pkg._id,
    }));
    setItems((prev) => {
      // Drop the single untouched placeholder row (if that's all there is)
      // instead of leaving a stray blank item alongside the real ones.
      const base = prev.length === 1 && !prev[0].description.trim() && !prev[0].packageId ? [] : prev;
      return [...base, ...newItems];
    });
  };

  const updateItem = (idx, field, value) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };
  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (idx) => setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  // Items are always THB — these are a preview of what the generated PDF will
  // show (its own conversion happens server-side at render time, see
  // services/salesDocumentRenderer.js), shown regardless of the selected
  // currency so staff can see all three before generating.
  const totalAmount = items.reduce((sum, it) => sum + itemAmount(it), 0);
  const totalLAK = exchangeRate ? convertAmount(totalAmount, "THB", "LAK", exchangeRate) : null;
  const totalUSD = exchangeRate ? convertAmount(totalAmount, "THB", "USD", exchangeRate) : null;

  const canSubmit = customer.name.trim() && items.every((it) => it.description.trim() && Number(it.qty) > 0) && !isSubmitting;

  const buildPayload = () => ({
    docType,
    customer,
    items,
    currency,
    hideFees,
    hideRate,
    termOfPayment: docType === "invoice" ? termOfPayment : undefined,
    dueDate: docType === "invoice" && dueDate ? dueDate : undefined,
    note,
  });

  // Step 1: render a watermarked, not-yet-saved PDF for review — this does
  // NOT create a document or advance the doc-number sequence.
  const handlePreview = () => {
    if (!canSubmit) return;
    setIsPreviewing(true);
    setPreviewUrl(null);
    setPreviewOpen(true);
    PreviewSalesDocumentPdf(buildPayload()).then((res) => {
      setIsPreviewing(false);
      if (!res.success) {
        setPreviewOpen(false);
        Swal.fire({ icon: "error", title: t("salesDocuments.generateError"), text: res.message });
        return;
      }
      setPreviewUrl(res.url);
    });
  };

  // Step 2: staff confirms from the preview dialog — only now is the
  // document actually saved and the real doc number assigned. When editing a
  // draft, that draft is issued (with the form's latest state) instead of
  // creating a second document.
  const handleConfirm = () => {
    setIsSubmitting(true);
    const request = editingDraftId ? IssueSalesDocumentDraft(editingDraftId, buildPayload()) : CreateSalesDocument(buildPayload());
    request.then(async (res) => {
      setIsSubmitting(false);
      if (!res.success) {
        Swal.fire({ icon: "error", title: t("salesDocuments.generateError"), text: res.message });
        return;
      }
      setPreviewOpen(false);
      onCreated?.();
      const pdfRes = await GetSalesDocumentPdfBlobUrl(res.data._id);
      if (pdfRes.success) window.open(pdfRes.url, "_blank");
      Swal.fire({
        icon: "success",
        text: t("salesDocuments.generateSuccess", { docNumber: res.data.docNumber }),
        timer: 2500, toast: true, position: "top-right", showConfirmButton: false,
      });
      resetForm();
    });
  };

  // Saves the form as-is (incomplete is fine) without consuming a doc number
  // — creates the draft on first save, then keeps updating that same draft.
  // Also offered from the preview dialog, which closes on a successful save.
  const handleSaveDraft = ({ closePreview = false } = {}) => {
    setIsSavingDraft(true);
    const request = editingDraftId ? UpdateSalesDocumentDraft(editingDraftId, buildPayload()) : SaveSalesDocumentDraft(buildPayload());
    request.then((res) => {
      setIsSavingDraft(false);
      if (!res.success) {
        Swal.fire({ icon: "error", title: t("salesDocuments.draftSaveError"), text: res.message });
        return;
      }
      setEditingDraftId(res.data._id);
      onDraftSaved?.(res.data);
      if (closePreview) setPreviewOpen(false);
      Swal.fire({ icon: "success", text: t("salesDocuments.draftSaved"), timer: 2000, toast: true, position: "top-right", showConfirmButton: false });
    });
  };

  const handleDiscardDraft = async () => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: t("salesDocuments.discardDraftConfirm"),
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: t("salesDocuments.discardDraft"),
      cancelButtonText: t("common.cancel"),
    });
    if (!confirm.isConfirmed) return;
    const res = await DiscardSalesDocumentDraft(editingDraftId);
    if (!res.success) {
      Swal.fire({ icon: "error", text: res.message });
      return;
    }
    resetForm();
    Swal.fire({ icon: "success", text: t("salesDocuments.draftDiscarded"), timer: 2000, toast: true, position: "top-right", showConfirmButton: false });
  };

  return (
    <>
      {editingDraftId && (
        <Alert
          severity="info"
          sx={{ mb: "22px", alignItems: "center" }}
          action={(
            <Box sx={{ display: "flex", gap: "8px" }}>
              <Button variant="outlined" color="error" size="small" onClick={handleDiscardDraft}>
                {t("salesDocuments.discardDraft")}
              </Button>
              <Button variant="outlined" color="secondary" size="small" onClick={resetForm}>
                {t("salesDocuments.newDocument")}
              </Button>
            </Box>
          )}
        >
          {t("salesDocuments.editingDraft")}
        </Alert>
      )}

      <BentoCard title={t("salesDocuments.customerSection")} sx={{ mb: "22px" }} bodySx={{ p: "20px 22px" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "24px", alignItems: "center", mb: "16px" }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t("salesDocuments.docType")}</InputLabel>
            <Select label={t("salesDocuments.docType")} value={docType} onChange={(e) => setDocType(e.target.value)}>
              <MenuItem value="receipt">{t("salesDocuments.docTypeReceipt")}</MenuItem>
              <MenuItem value="quotation">{t("salesDocuments.docTypeQuotation")}</MenuItem>
              <MenuItem value="invoice">{t("salesDocuments.docTypeInvoice")}</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t("salesDocuments.currency")}</InputLabel>
            <Select label={t("salesDocuments.currency")} value={currency} onChange={(e) => setCurrency(e.target.value)}>
              <MenuItem value="THB">THB</MenuItem>
              <MenuItem value="LAK">LAK</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
            </Select>
          </FormControl>

          {docType === "receipt" && (
            <>
              <CustomerSearchAutocomplete
                value={selectedCustomer}
                onChange={handleCustomerChange}
                label={t("salesDocuments.selectCustomer")}
                size="small"
                sx={{ minWidth: 280, flex: "1 1 320px" }}
              />
              <Button
                variant="outlined" color="secondary"
                startIcon={<Inventory2OutlinedIcon />}
                disabled={!selectedCustomer}
                onClick={() => setPickerOpen(true)}
              >
                {t("salesDocuments.browsePackages")}
              </Button>
            </>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: "16px" }}>
          {t("salesDocuments.currencyHelp")}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: "16px" }}>
          <TextField
            label={t("salesDocuments.customerName")}
            value={customer.name}
            required
            disabled={docType === "receipt"}
            onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))}
          />
          <TextField
            label={t("salesDocuments.customerAddress")}
            value={customer.address}
            onChange={(e) => setCustomer((c) => ({ ...c, address: e.target.value }))}
          />
          <TextField
            label={t("salesDocuments.customerContact")}
            value={customer.contact}
            onChange={(e) => setCustomer((c) => ({ ...c, contact: e.target.value }))}
          />
        </Box>
      </BentoCard>

      <BentoCard title={t("salesDocuments.itemsSection")} sx={{ mb: "22px" }} bodySx={{ p: "20px 22px" }}>
        {items.map((item, idx) => (
          <Box key={idx} sx={{ display: "grid", gridTemplateColumns: "3fr 1fr 1fr 1fr 1fr auto", gap: "10px", mb: "12px", alignItems: "center" }}>
            <TextField
              size="small"
              label={t("salesDocuments.columnDescription")}
              value={item.description}
              onChange={(e) => updateItem(idx, "description", e.target.value)}
            />
            <TextField
              size="small" type="number"
              label={t("salesDocuments.columnQty")}
              value={item.qty}
              onChange={(e) => updateItem(idx, "qty", e.target.value)}
            />
            <TextField
              size="small" type="number"
              label={t("salesDocuments.columnPrice")}
              value={item.price}
              onChange={(e) => updateItem(idx, "price", e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">{SYM.THB}</InputAdornment> }}
            />
            <TextField
              size="small" type="number"
              label={t("salesDocuments.columnDeliveryFee")}
              value={item.deliveryFee}
              onChange={(e) => updateItem(idx, "deliveryFee", e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">{SYM.THB}</InputAdornment> }}
            />
            <TextField
              size="small" type="number"
              label={t("salesDocuments.columnShippingFee")}
              value={item.shippingFee}
              onChange={(e) => updateItem(idx, "shippingFee", e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">{SYM.THB}</InputAdornment> }}
            />
            <IconButton size="small" onClick={() => removeItem(idx)} disabled={items.length === 1}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
            <Box sx={{ gridColumn: "1 / -1", textAlign: "right", fontSize: "0.8rem", color: "text.secondary" }}>
              {t("salesDocuments.columnAmount")}: {SYM.THB}{fmtMoney(itemAmount(item), "THB")}
            </Box>
          </Box>
        ))}

        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
          <Button variant="outlined" color="secondary" startIcon={<AddIcon />} onClick={addItem} sx={{ mt: "4px" }}>
            {t("salesDocuments.addItem")}
          </Button>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px 28px" }}>
            <Box>
              <FormControlLabel
                control={<Switch color="secondary" checked={hideFees} onChange={(e) => setHideFees(e.target.checked)} />}
                label={t("salesDocuments.hideFees")}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: "44px", mt: "-6px" }}>
                {t("salesDocuments.hideFeesHelp")}
              </Typography>
            </Box>
            <Box>
              <FormControlLabel
                control={<Switch color="secondary" checked={hideRate} onChange={(e) => setHideRate(e.target.checked)} />}
                label={t("salesDocuments.hideRate")}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: "44px", mt: "-6px" }}>
                {t("salesDocuments.hideRateHelp")}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: "18px" }} />

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Box sx={{ minWidth: 260 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", py: "4px" }}>
              <Typography color={currency === "THB" ? "secondary" : "text.secondary"} fontWeight={currency === "THB" ? 700 : 400}>
                {t("salesDocuments.totalAmount")} (THB)
              </Typography>
              <Typography color={currency === "THB" ? "secondary" : "text.secondary"} fontWeight={currency === "THB" ? 700 : 400}>
                {SYM.THB}{fmtMoney(totalAmount, "THB")}
              </Typography>
            </Box>
            {totalLAK != null && (
              <Box sx={{ display: "flex", justifyContent: "space-between", py: "2px" }}>
                <Typography variant="body2" color={currency === "LAK" ? "secondary" : "text.secondary"} fontWeight={currency === "LAK" ? 700 : 400}>≈ LAK</Typography>
                <Typography variant="body2" color={currency === "LAK" ? "secondary" : "text.secondary"} fontWeight={currency === "LAK" ? 700 : 400}>
                  ₭{totalLAK.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </Typography>
              </Box>
            )}
            {totalUSD != null && (
              <Box sx={{ display: "flex", justifyContent: "space-between", py: "2px" }}>
                <Typography variant="body2" color={currency === "USD" ? "secondary" : "text.secondary"} fontWeight={currency === "USD" ? 700 : 400}>≈ USD</Typography>
                <Typography variant="body2" color={currency === "USD" ? "secondary" : "text.secondary"} fontWeight={currency === "USD" ? 700 : 400}>
                  ${totalUSD.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </BentoCard>

      {docType === "invoice" && (
        <BentoCard sx={{ mb: "22px" }} bodySx={{ p: "20px 22px" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "16px" }}>
            <TextField
              label={t("salesDocuments.termOfPayment")}
              value={termOfPayment}
              onChange={(e) => setTermOfPayment(e.target.value)}
              placeholder="e.g. 5 days"
            />
            <TextField
              type="date"
              label={t("salesDocuments.dueDate")}
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </BentoCard>
      )}

      <BentoCard sx={{ mb: "22px" }} bodySx={{ p: "20px 22px" }}>
        <TextField
          fullWidth multiline minRows={2}
          label={t("salesDocuments.note")}
          value={note}
          onChange={(e) => { noteTouchedRef.current = true; setNote(e.target.value); }}
        />
      </BentoCard>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
        <Button
          variant="contained" color="secondary"
          startIcon={<VisibilityIcon />}
          disabled={!canSubmit || isPreviewing}
          onClick={handlePreview}
        >
          {t("salesDocuments.preview")}
        </Button>
        <Button
          variant="outlined" color="secondary"
          startIcon={<SaveOutlinedIcon />}
          disabled={isSavingDraft || isSubmitting}
          onClick={() => handleSaveDraft()}
        >
          {t("salesDocuments.saveDraft")}
        </Button>
      </Box>

      <PackagePickerDialog
        open={pickerOpen}
        customerId={selectedCustomer?._id}
        excludePackageIds={items.filter((it) => it.packageId).map((it) => it.packageId)}
        onClose={() => setPickerOpen(false)}
        onConfirm={handleAddPackages}
      />

      <PreviewDialog
        open={previewOpen}
        pdfUrl={previewUrl}
        isConfirming={isSubmitting}
        onClose={() => setPreviewOpen(false)}
        onConfirm={handleConfirm}
        onSaveDraft={() => handleSaveDraft({ closePreview: true })}
        isSavingDraft={isSavingDraft}
      />
    </>
  );
};

export default CreateTab;
